import { useQuery } from 'hooks/useCustomQuery';
import { useIsMutating, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import getTriggerById from 'API/Triggers/getTriggerByID';
import { toast } from 'react-toastify';

const useGetTriggerByID = (
    triggerId,
    setTrigger,
    resetToMainTable,
    isEditMode,
    isCopy,
    setCopyingTriggerName,
) => {
    const { currentScope } = useSelector((state) => state.login);
    const queryClient = useQueryClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const deletedKey = ['deleteTrigger', triggerId];

    const isCurrentlyDeleting =
        useIsMutating({
            mutationKey: deletedKey,
        }) > 0;

    const wasRecentlyDeleted = useMemo(() => {
        const mutationCache = queryClient.getMutationCache();
        const now = Date.now();
        const oneMinuteAgo = now - 60000;

        return mutationCache.getAll().some((mutation) => {
            const mutationKey = mutation.options.mutationKey;
            const submittedAt = mutation.state.submittedAt;

            return (
                mutationKey &&
                JSON.stringify(mutationKey) === JSON.stringify(deletedKey) &&
                submittedAt &&
                submittedAt >= oneMinuteAgo
            );
        });
    }, [queryClient, deletedKey]);

    const isDeletedOrDeleting = isCurrentlyDeleting || wasRecentlyDeleted;

    const isEnabled =
        Boolean(triggerId) &&
        (isEditMode === true || isCopy === true) &&
        triggerId !== null &&
        triggerId !== undefined &&
        !isDeletedOrDeleting;
    return useQuery({
        queryKey: ['getTriggersByID', triggerId],
        queryFn: async () => {
            if (!triggerId || !isEnabled || isDeletedOrDeleting) {
                throw new Error('Query conditions not met');
            }

            try {
                const data = await getTriggerById(triggerId, currentScope).then(
                    (res) => {
                        if (isEnabled && setTrigger && !isDeletedOrDeleting) {
                            setTrigger(
                                isCopy
                                    ? { ...res, name: 'Copy of ' + res?.name }
                                    : res,
                            );
                        }
                        if (
                            isEnabled &&
                            isCopy &&
                            setCopyingTriggerName &&
                            !isDeletedOrDeleting
                        ) {
                            setCopyingTriggerName(res?.name);
                        }
                        return res;
                    },
                );
                return data;
            } catch (error) {
                const errorMessage = `Failed to get trigger: ${error?.response?.data?.message}`;
                if (!isDeletedOrDeleting) {
                    toast.error(errorMessage);
                    if (resetToMainTable && isEnabled) {
                        resetToMainTable();
                    }
                }
                throw new Error(errorMessage);
            }
        },
        enabled: isEnabled,
        refetchOnWindowFocus: false,
        staleTime: 0,
        retry: false,
    });
};

export default useGetTriggerByID;
